import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import Header from '../../components/ui/Header';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

// Lazy load dashboard components for better performance
const KPICards = React.lazy(() => import('./components/KPICards'));
const ReviewsPanel = React.lazy(() => import('./components/ReviewsPanel'));
const ReviewTrendsChart = React.lazy(() => import('./components/ReviewTrendsChart'));
const RatingDistribution = React.lazy(() => import('./components/RatingDistribution'));
const RecentActivity = React.lazy(() => import('./components/RecentActivity'));

// Component loading skeleton for lazy-loaded components
const ComponentLoadingSkeleton = () => (
  <div className="bg-card rounded-lg border border-border p-6 animate-pulse">
    <div className="space-y-4">
      <div className="h-6 bg-muted rounded w-1/3"></div>
      <div className="h-32 bg-muted rounded"></div>
      <div className="h-4 bg-muted rounded w-1/2"></div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user, loading, userProfile, fetchUserProfile } = useAuth();
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardError, setDashboardError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [dashboardPreferences, setDashboardPreferences] = useState({
    showKPI: true,
    showCharts: true,
    showActivity: true,
    showReviews: true,
    layout: 'default' // 'default', 'compact', 'expanded'
  });
  const [customizationMode, setCustomizationMode] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Redirect to onboarding if not completed
  useEffect(() => {
    if (!loading && user && userProfile && !userProfile.onboarding_completed) {
      navigate('/onboarding');
    }
  }, [user, userProfile, loading, navigate]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const formatCurrentTime = () => {
    return currentTime.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setDashboardError(null);
    
    try {
      // Trigger refresh for all dashboard components
      setLastRefresh(new Date());
      
      // You can add specific refresh logic here if needed
      // For now, components will handle their own refresh based on lastRefresh
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate refresh delay
    } catch (error) {
      setDashboardError('Failed to refresh dashboard data');
    } finally {
      setRefreshing(false);
    }
  }, []);

  const formatLastRefresh = () => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - lastRefresh) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    return lastRefresh.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleDashboardExport = useCallback(async () => {
    setExportLoading(true);
    try {
      const dashboardData = {
        exportedAt: new Date().toISOString(),
        user: userProfile?.full_name || user?.email,
        preferences: dashboardPreferences,
        lastRefresh: lastRefresh.toISOString(),
        metadata: {
          version: '1.0',
          format: 'json'
        }
      };
      
      const blob = new Blob([JSON.stringify(dashboardData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      setDashboardError('Failed to export dashboard data');
    } finally {
      setExportLoading(false);
    }
  }, [dashboardPreferences, lastRefresh, userProfile, user]);

  const toggleCustomization = () => {
    setCustomizationMode(prev => !prev);
  };

  const updatePreferences = (key, value) => {
    setDashboardPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Save to localStorage for persistence
    const updatedPrefs = { ...dashboardPreferences, [key]: value };
    localStorage.setItem('dashboardPreferences', JSON.stringify(updatedPrefs));
  };

  const resetToDefaults = () => {
    const defaultPrefs = {
      showKPI: true,
      showCharts: true,
      showActivity: true,
      showReviews: true,
      layout: 'default'
    };
    setDashboardPreferences(defaultPrefs);
    localStorage.setItem('dashboardPreferences', JSON.stringify(defaultPrefs));
  };

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPrefs = localStorage.getItem('dashboardPreferences');
    if (savedPrefs) {
      try {
        const parsedPrefs = JSON.parse(savedPrefs);
        setDashboardPreferences(parsedPrefs);
      } catch (error) {
        console.error('Error loading dashboard preferences:', error);
      }
    }
  }, []);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if no user
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header 
        onSidebarToggle={handleSidebarToggle}
        sidebarExpanded={sidebarExpanded}
      />

      {/* Sidebar */}
      <EnhancedSidebar 
        expanded={sidebarExpanded}
        onToggle={handleSidebarToggle}
      />

      {/* Main Content */}
      <main className={`pt-16 transition-all duration-300 ease-in-out ${
        sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
      }`}>
        <EnhancedBreadcrumb />
        
        <div className="max-w-none mx-auto p-3 sm:p-4 md:p-6 lg:max-w-[94%]">

          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                  Review Dashboard
                </h1>
                <p className="text-sm sm:text-base text-text-secondary">
                  Welcome back, {userProfile?.full_name || user?.email?.split('@')[0]}! Here's your review performance overview.
                </p>
              </div>
              <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-2">
                <div className="text-sm text-text-muted order-2 sm:order-1">
                  <div className="hidden sm:block">{formatCurrentTime()}</div>
                  <div className="text-xs">Last updated: {formatLastRefresh()}</div>
                </div>
                
                <div className="flex items-center space-x-2 order-1 sm:order-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDashboardExport}
                    disabled={exportLoading}
                    iconName={exportLoading ? "Loader2" : "Download"}
                    className={`text-text-secondary hover:text-text-primary ${exportLoading ? "animate-spin" : ""}`}
                  >
                    Export
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleCustomization}
                    iconName={customizationMode ? "X" : "Settings"}
                    className={`text-text-secondary hover:text-text-primary ${customizationMode ? "bg-primary-50 text-primary" : ""}`}
                  >
                    {customizationMode ? "Done" : "Customize"}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    iconName={refreshing ? "Loader2" : "RefreshCw"}
                    className={`text-text-secondary hover:text-text-primary ${refreshing ? "animate-spin" : ""}`}
                  >
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Error Banner */}
            {dashboardError && (
              <div className="mt-4 bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon name="AlertCircle" size={20} className="text-destructive" />
                    <div>
                      <h3 className="text-sm font-medium text-destructive">Dashboard Error</h3>
                      <p className="text-xs text-destructive/70">{dashboardError}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDashboardError(null)}
                    iconName="X"
                    className="text-destructive hover:text-destructive/80"
                  />
                </div>
              </div>
            )}
          </div>


          {/* Customization Panel */}
          {customizationMode && (
            <div className="mb-6 bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Dashboard Preferences</h3>
                  <p className="text-sm text-muted-foreground">Customize your dashboard layout and components</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetToDefaults}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Reset to Defaults
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Component Visibility */}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Dashboard Components</h4>
                  <div className="space-y-3">
                    {[
                      { key: 'showKPI', label: 'KPI Cards', description: 'Key performance indicators' },
                      { key: 'showCharts', label: 'Analytics Charts', description: 'Trends and distributions' },
                      { key: 'showActivity', label: 'Recent Activity', description: 'Live activity feed' },
                      { key: 'showReviews', label: 'Reviews Panel', description: 'Customer feedback' }
                    ].map((item) => (
                      <label key={item.key} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dashboardPreferences[item.key]}
                          onChange={(e) => updatePreferences(item.key, e.target.checked)}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                        />
                        <div>
                          <div className="text-sm font-medium text-foreground">{item.label}</div>
                          <div className="text-xs text-muted-foreground">{item.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Layout Options */}
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Layout Style</h4>
                  <div className="space-y-2">
                    {[
                      { value: 'default', label: 'Default', description: 'Standard layout' },
                      { value: 'compact', label: 'Compact', description: 'Reduced spacing' },
                      { value: 'expanded', label: 'Expanded', description: 'More breathing room' }
                    ].map((layout) => (
                      <label key={layout.value} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="layout"
                          value={layout.value}
                          checked={dashboardPreferences.layout === layout.value}
                          onChange={(e) => updatePreferences('layout', e.target.value)}
                          className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
                        />
                        <div>
                          <div className="text-sm font-medium text-text-primary">{layout.label}</div>
                          <div className="text-xs text-text-muted">{layout.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* KPI Cards - Review focused metrics */}
          {dashboardPreferences.showKPI && (
            <div className={`mb-${dashboardPreferences.layout === 'compact' ? '4' : dashboardPreferences.layout === 'expanded' ? '12' : '8'}`}>
              <Suspense fallback={<ComponentLoadingSkeleton />}>
                <KPICards />
              </Suspense>
            </div>
          )}

          {/* Main Analytics Grid */}
          {dashboardPreferences.showCharts && (
            <div className={`grid grid-cols-1 xl:grid-cols-2 gap-${dashboardPreferences.layout === 'compact' ? '3 sm:gap-4 xl:gap-6' : dashboardPreferences.layout === 'expanded' ? '6 sm:gap-8 xl:gap-12' : '4 sm:gap-6 xl:gap-8'} mb-${dashboardPreferences.layout === 'compact' ? '4 sm:mb-6' : dashboardPreferences.layout === 'expanded' ? '8 sm:mb-12' : '6 sm:mb-8'}`}>
              {/* Review Trends Chart */}
              <div className="xl:col-span-1">
                <Suspense fallback={<ComponentLoadingSkeleton />}>
                  <ReviewTrendsChart />
                </Suspense>
              </div>

              {/* Rating Distribution */}
              <div className="xl:col-span-1">
                <Suspense fallback={<ComponentLoadingSkeleton />}>
                  <RatingDistribution />
                </Suspense>
              </div>
            </div>
          )}

          {/* Recent Activity and Reviews Grid */}
          {(dashboardPreferences.showActivity || dashboardPreferences.showReviews) && (
            <div className={`grid grid-cols-1 xl:grid-cols-3 gap-${dashboardPreferences.layout === 'compact' ? '3 sm:gap-4 xl:gap-6' : dashboardPreferences.layout === 'expanded' ? '6 sm:gap-8 xl:gap-12' : '4 sm:gap-6 xl:gap-8'} mb-${dashboardPreferences.layout === 'compact' ? '4 sm:mb-6' : dashboardPreferences.layout === 'expanded' ? '8 sm:mb-12' : '6 sm:mb-8'}`}>
              {/* Recent Activity - Full width on mobile/tablet, 1/3 on desktop */}
              {dashboardPreferences.showActivity && (
                <div className={`${dashboardPreferences.showReviews ? 'order-2 xl:order-1 xl:col-span-1' : 'col-span-full'}`}>
                  <Suspense fallback={<ComponentLoadingSkeleton />}>
                    <RecentActivity refreshTrigger={lastRefresh} />
                  </Suspense>
                </div>
              )}

              {/* Reviews Panel - Full width on mobile/tablet, 2/3 on desktop */}
              {dashboardPreferences.showReviews && (
                <div className={`${dashboardPreferences.showActivity ? 'order-1 xl:order-2 xl:col-span-2' : 'col-span-full'}`}>
                  <Suspense fallback={<ComponentLoadingSkeleton />}>
                    <ReviewsPanel />
                  </Suspense>
                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default Dashboard;